import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { FileText, Plus, Edit2, Trash2, Save, X, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const TermsConditionsTab = ({ user }) => {
  const [termsList, setTermsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTerms, setEditingTerms] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    applies_to: []
  });

  const documentTypes = [
    { id: 'orders', label: 'Orders' },
    { id: 'bookings', label: 'Bookings' },
    { id: 'kitty', label: 'Kitty Schemes' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'invoices', label: 'Invoices' }
  ];

  useEffect(() => {
    loadTerms();
  }, []);

  const loadTerms = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/terms-conditions');
      setTermsList(response.data);
    } catch (error) {
      console.error('Error loading terms:', error);
      toast.error('Failed to load terms and conditions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTerms(null);
    setFormData({
      name: '',
      content: '',
      applies_to: []
    });
    setShowModal(true);
  };

  const handleEdit = (terms) => {
    setEditingTerms(terms);
    setFormData({
      name: terms.name,
      content: terms.content,
      applies_to: terms.applies_to || []
    });
    setShowModal(true);
  };

  const handleTypeToggle = (typeId) => {
    const newAppliesTo = formData.applies_to.includes(typeId)
      ? formData.applies_to.filter(t => t !== typeId)
      : [...formData.applies_to, typeId];
    
    setFormData({ ...formData, applies_to: newAppliesTo });
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Name is required');
        return;
      }
      if (!formData.content.trim()) {
        toast.error('Content is required');
        return;
      }

      if (editingTerms) {
        await axiosInstance.put(`/admin/terms-conditions/${editingTerms.tc_id}`, formData);
        toast.success('Terms updated successfully');
      } else {
        await axiosInstance.post('/admin/terms-conditions', formData);
        toast.success('Terms created successfully');
      }

      setShowModal(false);
      loadTerms();
    } catch (error) {
      console.error('Error saving terms:', error);
      toast.error('Failed to save terms');
    }
  };

  const handleDelete = async (tc_id) => {
    if (!window.confirm('Are you sure you want to delete these terms?')) return;

    try {
      await axiosInstance.delete(`/admin/terms-conditions/${tc_id}`);
      toast.success('Terms deleted successfully');
      loadTerms();
    } catch (error) {
      console.error('Error deleting terms:', error);
      toast.error('Failed to delete terms');
    }
  };

  const handleToggleActive = async (terms) => {
    try {
      await axiosInstance.put(`/admin/terms-conditions/${terms.tc_id}`, {
        is_active: !terms.is_active
      });
      toast.success(`Terms ${!terms.is_active ? 'activated' : 'deactivated'}`);
      loadTerms();
    } catch (error) {
      console.error('Error toggling terms:', error);
      toast.error('Failed to update terms status');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Terms & Conditions Management
            </h2>
            <p className="text-blue-100 mt-1 text-sm">Manage terms displayed on print formats</p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add New Terms
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> These terms will be automatically included in print formats (PDF exports) 
          for the selected document types. <strong>Each line will be displayed as a bullet point.</strong>
        </p>
        <p className="text-xs text-blue-700 mt-2">
          💡 Tip: Write each term on a new line. Empty lines will be ignored.
        </p>
      </div>

      {/* Terms List */}
      <div className="space-y-4">
        {termsList.map((terms) => (
          <div
            key={terms.tc_id}
            className={`bg-white rounded-xl shadow-md border-2 p-4 sm:p-6 transition-all ${
              terms.is_active ? 'border-blue-200' : 'border-gray-300 opacity-60'
            }`}
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-gray-800">{terms.name}</h3>
                  {terms.is_active && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                      Active
                    </span>
                  )}
                  {!terms.is_active && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {terms.applies_to?.map((type) => (
                    <span
                      key={type}
                      className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full"
                    >
                      {documentTypes.find(t => t.id === type)?.label || type}
                    </span>
                  ))}
                  {(!terms.applies_to || terms.applies_to.length === 0) && (
                    <span className="text-xs text-gray-500 italic">No document types selected</span>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 border border-gray-200 max-h-40 overflow-y-auto">
                  {terms.content.split('\n').filter(line => line.trim()).map((line, idx) => (
                    <div key={idx} className="flex gap-2 mb-1">
                      <span className="text-blue-600 flex-shrink-0">•</span>
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Created by {terms.created_by} • ID: {terms.tc_id}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-col gap-2 flex-shrink-0">
                <button
                  onClick={() => handleEdit(terms)}
                  className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleToggleActive(terms)}
                  className={`p-2 rounded-lg transition-colors ${
                    terms.is_active
                      ? 'bg-orange-100 hover:bg-orange-200 text-orange-600'
                      : 'bg-green-100 hover:bg-green-200 text-green-600'
                  }`}
                  title={terms.is_active ? 'Deactivate' : 'Activate'}
                >
                  <CheckCircle className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(terms.tc_id)}
                  className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {termsList.length === 0 && (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 mb-4">No terms and conditions created yet</p>
            <button
              onClick={handleAdd}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors"
            >
              Create First Terms
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">
                {editingTerms ? 'Edit Terms & Conditions' : 'Add New Terms & Conditions'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Name / Title
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Order Terms, Booking Terms"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Applies To */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Applies To (Select Document Types)
                </label>
                <div className="flex flex-wrap gap-2">
                  {documentTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => handleTypeToggle(type.id)}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                        formData.applies_to.includes(type.id)
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Terms & Conditions Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter each term on a new line&#10;Example:&#10;All orders are subject to availability&#10;Payment must be made in full before delivery&#10;Returns accepted within 7 days with original receipt"
                  rows={12}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  <strong>📋 Each line will appear as a bullet point</strong> in print formats. Empty lines are ignored.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-colors"
                >
                  <Save className="w-5 h-5" />
                  {editingTerms ? 'Update Terms' : 'Create Terms'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsConditionsTab;
